import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

import clsx from 'clsx';

import css from './index.module.less';

/**
 * 动画效果类型
 * - fade: 淡入（原地出现）
 * - slideUp: 从下往上滑入
 * - slideDown: 从上往下滑入
 * - scale: 缩放出现
 * - blur: 模糊渐显
 */
export type RevealAnimationType = 'fade' | 'slideUp' | 'slideDown' | 'scale' | 'blur';

export interface RevealTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** 要显示的文本 */
  text: string;
  /** 分几次显示完成，默认为 5 */
  steps?: number;
  /** 每次显示的间隔时间(ms)，默认为 150 */
  interval?: number;
  /** 初始延迟时间(ms)，默认为 0 */
  delay?: number;
  /** 字符显示时的动画持续时间(ms)，默认为 300 */
  charAnimationDuration?: number;
  /** 是否只触发一次，默认为 true */
  once?: boolean;
  /** 进入视口的阈值，默认为 1（完全可见时触发） */
  threshold?: number;
  /** 动画效果类型，默认为 'fade' */
  animation?: RevealAnimationType;
}

/**
 * RevealText - 随机渐显文本组件
 *
 * 当组件进入视口时，文本字符会以随机顺序分批次显示
 */
export function RevealText({
  text,
  steps = 5,
  interval = 150,
  delay = 0,
  className,
  style,
  charAnimationDuration = 300,
  once = true,
  threshold = 1,
  animation = 'fade',
  ...restProps
}: RevealTextProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isInView, setIsInView] = useState(false);


  // 生成随机排列的字符索引
  const shuffledIndices = useMemo(() => {
    const indices = text
      .split('')
      .map((char, index) => ({ char, index }))
      .filter(({ char }) => char !== ' ') // 空格不参与随机，直接显示
      .map(({ index }) => index);

    // Fisher-Yates 洗牌算法
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    return indices;
  }, [text]);

  // 将索引分成指定步数的批次
  const batches = useMemo(() => {
    const result: number[][] = [];
    const batchSize = Math.ceil(shuffledIndices.length / steps);

    for (let i = 0; i < shuffledIndices.length; i += batchSize) {
      result.push(shuffledIndices.slice(i, i + batchSize));
    }

    return result;
  }, [shuffledIndices, steps]);

  // 开始动画
  const startAnimation = useCallback(() => {
    if (once && hasTriggered) return;

    setHasTriggered(true);
    setVisibleIndices(new Set());

    // 延迟开始
    const timeoutId = setTimeout(() => {
      batches.forEach((batch, batchIndex) => {
        setTimeout(() => {
          setVisibleIndices((prev) => {
            const next = new Set(prev);
            batch.forEach((index) => next.add(index));
            return next;
          });
        }, batchIndex * interval);
      });
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [batches, interval, delay, once, hasTriggered]);

  // 重置动画
  const resetAnimation = useCallback(() => {
    if (!once) {
      setVisibleIndices(new Set());
      setHasTriggered(false);
    }
  }, [once]);

  // Intersection Observer 监听进入视口
  // 使用两个阈值：1 表示完全可见，0 表示完全消失
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 完全可见时触发动画
          if (entry.intersectionRatio >= threshold) {
            setIsInView(true);
          }
          // 完全消失时重置动画
          else if (entry.intersectionRatio === 0) {
            setIsInView(false);
          }
        });
      },
      { threshold: [0, threshold] }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  // 当 text 变化时，重置状态并重新开始动画
  useEffect(() => {
    setVisibleIndices(new Set());
    setHasTriggered(false);
  }, [text]);

  // 根据视口状态控制动画
  useEffect(() => {
    if (isInView) {
      startAnimation();
    } else {
      resetAnimation();
    }
  }, [isInView, startAnimation, resetAnimation]);

  return (
    <span
      ref={containerRef}
      className={clsx(css['reveal-text'], className)}
      style={style}
      {...restProps}
    >
      {text.split('').map((char, index) => {
        const isSpace = char === ' ';
        const isVisible = isSpace || visibleIndices.has(index);

        return (
          <span
            key={index}
            className={clsx(
              css['reveal-char'],
              css[`animation-${animation}`],
              {
                [css['visible']]: isVisible,
                [css['space']]: isSpace,
              }
            )}
            style={{
              transitionDuration: `${charAnimationDuration}ms`,
            }}
          >
            {char}
          </span>
        );
      })}
    </span>
  );
}

export default RevealText;
